/**
 * Applicant review page — the canonical operator-facing surface served at
 * /applicant/:id. Backend gaps and their frontend fallbacks are documented
 * in docs/applicant-page-b-backend-handoff.md — every `(review as any).<field>`
 * lookup in this file corresponds to a row in that table.
 */

import { AnimatePresence, motion } from "framer-motion";
import { initClarity } from "@/lib/clarity";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  Circle,

  FileText,
  Info,
  Loader2,
  MessageSquarePlus,
  Send,
  Sparkles,
  ShieldCheck,
  XCircle,
} from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { useCases } from "@/hooks/useCases";
import { useCaseReview } from "@/hooks/useCaseReview";
import { useInquiryResult } from "@/hooks/useInquiryResult";
import { useInternalNotes } from "@/hooks/useInternalNotes";
import { Skeleton } from "@/components/ui/skeleton";
import { reviewerApi, type DecisionInput, type CaseReview, type CaseReviewSource } from "@/lib/api";
import {
  REGULATORY_NOTE,
  buildWhatsHappening,
  formatAbsoluteDateTime,
  formatRelativeTime,
  getCaseStatus,
  detailStatusLabel,
  reviewDecisionCopy,
  statusLabel,
} from "@/lib/caseStatus";
import { resolveFollowUpReason } from "@/lib/followUpReasons";
import {
  describeApplicantIdvState,
  describeIdvNameBinding,
  parseApplicantIdvState,
  parseIdvNameBinding,
} from "@/lib/applicantIdvState";
import { RequiredIdvReviewCard } from "@/components/RequiredIdvReviewCard";
import { hasRequiredIdvToShow, parseRequiredIdv } from "@/lib/requiredIdv";
import { ApplicantIDVStateBadge } from "@/components/ApplicantIDVStateBadge";
import { useOperatorVertical } from "@/hooks/useOperatorMe";
import { operatorHome } from "@/lib/operatorHome";
import { toTitleCase } from "@/lib/utils";
import {
  copyForVertical,
  isRecruiting,
  queueBackLabel,
  showsAiReviewSummary,
  showsApplicantRating,
  canShowApplicantRating,
  showsRentalScopeNote,
  showsFollowUpReason,
  type Vertical,
  type VerticalCopy,
} from "@/lib/verticalCopy";


const NOT_VERIFIED_REASONS: Array<{ value: string; label: string; desc: string }> = [
  {
    value: "linked_account_mismatch",
    label: "Linked Account Mismatch",
    desc: "Use when the applicant connected a live account, but the name or email on that account belongs to someone else.",
  },
  {
    value: "insufficient_digital_signals",
    label: "Insufficient Digital Signals",
    desc: "Use when the applicant has a thin file — they went through the flow but didn't connect enough verified accounts to establish identity confidence.",
  },
  {
    value: "vouch_verification_failed",
    label: "Vouch / Reference Verification Failed",
    desc: "Use when digital signals look fine but the human guarantor or professional voucher failed to confirm the applicant's identity or legitimacy.",
  },
  {
    value: "applicant_unresponsive",
    label: "Applicant Unresponsive / Abandoned",
    desc: "Use when the application stalled because the applicant refused to complete required steps, link accounts, or respond to follow-up.",
  },
  {
    value: "other",
    label: "Other (Requires Internal Note)",
    desc: "Use only for edge cases. An internal note explaining the reason is required for the audit trail.",
  },
];

/**
 * The reasons a recruiter can record findings under: only the two that
 * describe the process, not the person (review call 2026-10-15). Leasing keeps
 * the full list. Every value stays in NOT_VERIFIED_REASONS, so a case recorded
 * earlier under another reason still shows its label.
 */
const RECRUITING_REASON_VALUES = ["insufficient_digital_signals", "applicant_unresponsive"];

function notVerifiedReasonsFor(recruiting: boolean) {
  return recruiting
    ? NOT_VERIFIED_REASONS.filter((r) => RECRUITING_REASON_VALUES.includes(r.value))
    : NOT_VERIFIED_REASONS;
}

/**
 * Recruiting wording for a reason whose leasing description talks about
 * identity confidence. The reason values and labels are the same in both
 * verticals; only the helper line under the select differs.
 */
const RECRUITING_REASON_DESC: Record<string, string> = {
  insufficient_digital_signals:
    "Use when the applicant went through the flow but provided too little evidence to review.",
};

function notVerifiedReasonDesc(value: string, recruiting: boolean): string | undefined {
  if (recruiting && RECRUITING_REASON_DESC[value]) return RECRUITING_REASON_DESC[value];
  return NOT_VERIFIED_REASONS.find((r) => r.value === value)?.desc;
}

// ------------------------------------------------------------------
// Local presentation helpers for the applicant review page.
// ------------------------------------------------------------------


type SourceStatus =
  | "connected"
  | "confirmed"
  | "not_connected"
  | "pending"
  | "not_available"
  | "failed"
  | "disconnected"
  | "not_confirmed";

const SOURCE_BADGE: Record<SourceStatus, { text: string; cls: string }> = {
  connected: { text: "Connected", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  confirmed: { text: "Confirmed", cls: "bg-sky-50 text-sky-800 border-sky-200" },
  not_connected: { text: "Not connected", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  pending: { text: "Pending", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  not_available: { text: "Not available", cls: "bg-slate-100 text-slate-500 border-slate-200" },
  // Backend brief §1 — render correctly the moment the API returns these
  // statuses. Until then, nothing in the payload maps here, so behaviour is
  // unchanged.
  failed: { text: "Attempt failed", cls: "bg-amber-50 text-amber-900 border-amber-300" },
  disconnected: { text: "Disconnected", cls: "bg-slate-100 text-slate-600 border-slate-300" },
  not_confirmed: { text: "Not confirmed", cls: "bg-rose-50 text-rose-800 border-rose-200" },
};

// Backend now suppresses unoffered-source evidence keys + scoring server-side
// (BACKEND_INTEGRATION.md §16.2 — driven by PLAID_INCOME_ENABLED, UBER_CLIENT_ID,
// and a permanent `contact.email_disposable` block). The frontend stop-gap
// filters that used to live here (HIDDEN_EVIDENCE_KEY_PATTERNS /
// HIDDEN_EVIDENCE_TEXT_PATTERNS) have been retired per §16.3.


// Generic backend-enum → operator-friendly label. Strips underscores and
// title-cases each word. Used for any raw enum string that leaks into the
// UI (source names, vouch status labels, etc.). Backend payload is
// unchanged — this is presentation only.
function prettifyLabel(rawInput: unknown): string {
  const raw = typeof rawInput === "string" ? rawInput : rawInput == null ? "" : String(rawInput);
  if (!raw) return raw;
  return raw
    .replace(/[_\-.]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (m) => m.toUpperCase());
}



function resolveSourceStatus(src: CaseReviewSource): SourceStatus {
  const raw = (src?.status ?? "").toString().toLowerCase();
  if (raw in SOURCE_BADGE) return raw as SourceStatus;
  const strength = (src?.strength ?? "").toString().toLowerCase();
  if (strength === "strong" || strength === "moderate") return "connected";
  if (strength === "limited") return "pending";
  return "connected";
}

const SOURCE_NAME_MAP: Record<string, string> = {
  "identity.name": "Name match",
  "identity.dob": "Date of birth check",
  "identity.ssn_hash": "SSN check (hashed)",
  "identity.address": "Address check",
  "contact.email": "Email Address",
  "contact.email_disposable": "Email type check",
  "contact.phone": "Phone check",
  "digital.email_history": "Email account history",
  "digital.social_account": "LinkedIn / social identity",
  "employment.source": "Employment source",
  "financial.bank": "Bank account",
  "human.vouch": "Human Vouch",
  "human_vouch": "Human Vouch",
  "vouch_completed": "Vouch Completed",
  "vouch completed": "Vouch Completed",
  "human.vouch.identity": "Voucher ID verified",
  "human.vouch.bank": "Voucher bank connected",
  "human.vouch.email": "Voucher email connected",
};

function prettySourceName(rawInput: unknown): string {
  const raw = typeof rawInput === "string" ? rawInput : rawInput == null ? "" : String(rawInput);
  if (!raw) return raw;
  if (SOURCE_NAME_MAP[raw]) return SOURCE_NAME_MAP[raw];

  // Strip trailing status words the backend sometimes appends
  // ("email_disposable on file", "Identity Name verified", etc.)
  const withoutStatus = raw
    .replace(/\s+(on file|verified|pending|missing|connected|not connected|confirmed|available|not available|hashed)$/i, "")
    .trim();
  if (SOURCE_NAME_MAP[withoutStatus]) return SOURCE_NAME_MAP[withoutStatus];

  const normalized = withoutStatus.toLowerCase().replace(/\s+/g, "_").replace(/-+/g, "_");
  if (SOURCE_NAME_MAP[normalized]) return SOURCE_NAME_MAP[normalized];

  const suffix = normalized.includes(".") ? normalized.split(".").pop()! : normalized;
  for (const prefix of ["contact", "identity", "digital", "financial", "human", "employment"]) {
    const key = `${prefix}.${suffix}`;
    if (SOURCE_NAME_MAP[key]) return SOURCE_NAME_MAP[key];
  }

  if (/^[a-z_]+\.[a-z_.]+$/.test(raw)) {
    const tail = raw.split(".").pop()!;
    return tail.replace(/[_-]+/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
  }
  // Final fallback — any raw enum with underscores becomes title-cased.
  if (/[_]/.test(raw)) return prettifyLabel(raw);
  return raw;
}


function buildSourceDetail(src: CaseReviewSource): string {
  // Backend gap GAP-3 — fall back to combining tenure + corroboration.
  if (typeof src?.detail === "string" && src.detail.trim()) return src.detail;
  const bits = [src?.corroboration, src?.tenure].filter(Boolean);
  return bits.join(" — ");
}

// `buildSignalOnlySources` retired per BACKEND_INTEGRATION.md §15.1 — the
// backend now surfaces non-OAuth social/Facebook-SDK evidence directly in
// `connected_sources[]`, so the frontend no longer needs to synthesize rows
// from `signals_summary`.

function cleanTimelineEvent(input: unknown, canShowRating: boolean): string {
  const s = typeof input === "string" ? input : input == null ? "" : String(input);
  const lc = s.toLowerCase();
  // ⚠️ Matches BOTH spellings, and must keep doing so. The same decision
  // reaches this function two ways: the ActivityEvent stream is fed in as
  // `ev.kind.replace(/_/g, " ")`, so it arrives as `identity confidence
  // established`, while `verification_timeline` passes its string through
  // untouched as `identity_confidence_established`. The two leasing branches
  // below only match the underscored form — which is why a de-underscored
  // result event falls through to the generic scrub at the bottom and renders
  // as "Identity confidence established". That is still a rating, it is still
  // one a recruiter must not see, and the ActivityEvent stream is the path
  // that WINS (`events = timeline.length > 0 ? timeline : legacyTimeline`).
  const isResultEvent =
    /decided:\s*proceed|identity[_ ]confidence[_ ]established|application\s+approved/.test(lc) ||
    /decided:\s*hold|identity[_ ]confidence[_ ]not[_ ]established|application\s+declined/.test(lc);
  // platform#583 — a recruiter reads the timeline as activity, not as a
  // verdict. Both result branches collapse to the label this same map already
  // uses for `result_decided`: the row still says a result exists and when,
  // and says nothing about what it was.
  //
  // ⚠️ Deliberately an EXISTING label, not a new one. The recruiting wording
  // for a case outcome is `CandidateStatusV1` and it is owed by
  // platform#534 — minting a second vocabulary here is the thing that
  // issue would then have to unpick.
  //
  // ⚠️ `canShowRating` folds in `useOperatorVertical().isResolved`, not just
  // `showsApplicantRating(vertical)`. The Identity Confidence and AI Review
  // Summary cards both hold a skeleton until the vertical resolves. An
  // in-flight `me()` query now reads as unknown (`verticalFrom(undefined)`),
  // which rates nobody, but the hold stays: it is what keeps a mocked or
  // cached leasing answer from painting before the real one lands. The Timeline card has no skeleton to hold; it is a collapsed
  // section available to open the instant the page mounts. Gating this
  // branch on `vertical` alone let a recruiter who opened the Timeline before
  // their membership resolved see "DigaVouch result — Verified" for that
  // window, the exact rating this predicate exists to hide.
  if (isResultEvent && !canShowRating) return "Result decided";
  if (/decided:\s*proceed|identity_confidence_established|application\s+approved/.test(lc)) return "DigaVouch result — Verified";
  if (/decided:\s*hold|identity_confidence_not_established|application\s+declined/.test(lc)) return "DigaVouch result — Not Verified";
  if (/request_more_info|additional verification/.test(lc)) return "Additional verification requested";
  if (/vouch confirmed/.test(lc)) return "Vouch confirmed";
  if (/vouch request|vouch_invited/.test(lc)) return "Vouch request sent";
  if (/email connected|email_verified|email_history_verified/.test(lc)) return "Email connected";
  if (/social connected|social_account_verified/.test(lc)) return "Social connected";
  if (/applicant started|applicant_started/.test(lc)) return "Applicant started";
  if (/\bconsent\b|consent_granted/.test(lc)) return "Applicant consent granted";
  if (/verification_link_sent|verification session created|inquiry created|verification link/.test(lc)) return "Verification link sent";
  if (/verification_submitted|application\s+submitted|verification\s+submitted/.test(lc)) return "Verification submitted";
  if (/case[_ ]closed/.test(lc)) return "Case closed";
  if (/result[_ ]decided/.test(lc)) return "Result decided";
  // Default: sentence-case (capitalize first letter) and scrub legacy words.
  const scrubbed = s.replace(/\bapproved\b/gi, "submitted").replace(/\bdeclined\b/gi, "closed");
  return scrubbed.charAt(0).toUpperCase() + scrubbed.slice(1);
}

// ------------------------------------------------------------------
// Derived fallbacks for the GAP fields (see handoff doc)
// ------------------------------------------------------------------

/**
 * The evidence card's data.
 *
 * `headline` and `summary` are the *rating* — "High Confidence", "DigaVouch
 * found high confidence based on the connected signals" — and they are
 * `null` on a surface that does not rate the applicant (platform#583).
 * Nullable rather than optional on purpose: an optional field invites a call
 * site to read it without thinking, while `string | null` makes the render
 * branch explicit and makes a re-added `{confidence.headline}` fail to
 * typecheck rather than fail to comply.
 *
 * `why` and `missing` are the evidence and are never stripped.
 */
interface ConfidencePanel {
  headline: string | null;
  summary: string | null;
  why: string[];
  missing: string[];
  suggested_action: string | null;
}

function deriveVouchState(review: CaseReview | null): "none" | "requested" | "confirmed" {
  const sources = review?.connected_sources ?? [];
  const vouchSources = sources.filter((s) => /vouch/i.test(String(s?.name ?? "")));
  if (vouchSources.some((s) => resolveSourceStatus(s) === "confirmed")) return "confirmed";
  if (vouchSources.length > 0) return "requested";
  const human = review?.evidence_tree?.human ?? [];
  const vouchTree = human.filter((i) => /vouch/i.test(String(i?.item ?? "")));
  if (vouchTree.some((i) => i.status === "verified")) return "confirmed";
  if (vouchTree.some((i) => i.status === "partial")) return "requested";
  return "none";
}

function applyVouchMissingLine(missing: string[], state: "none" | "requested" | "confirmed"): string[] {
  // Strip any vouch-related missing lines the backend or derived logic already
  // produced so we own the copy for this row.
  const filtered = missing.filter((line) => !/vouch/i.test(line));
  if (state === "confirmed") return filtered;
  const line = state === "none" ? "Vouch request not sent" : "Vouch reference not confirmed";
  if (!filtered.includes(line)) filtered.push(line);
  return filtered;
}

/**
 * The evidence card, from the backend's panel where there is one and derived
 * from the raw evidence where there is not.
 *
 * ⚠️ **`vertical` is the last word, applied to both paths.** The backend still
 * sends `confidence_panel` and `confidence_label` to every org, recruiting
 * included (checked against `origin/uat` 2026-09-18), so stripping the rating
 * only from the derived path would leave it rendering for exactly the orgs the
 * backend serves a panel for. A field that is delivered but unused is a field
 * someone renders again later; the backend half is platform#593.
 */
function deriveConfidencePanel(
  review: CaseReview | null,
  copy: VerticalCopy,
  vertical: Vertical,
): ConfidencePanel {
  const fromBackend = review?.confidence_panel as ConfidencePanel | undefined;
  const vouchState = deriveVouchState(review);
  const rates = showsApplicantRating(vertical);
  if (fromBackend) {
    return {
      ...fromBackend,
      headline: rates ? fromBackend.headline : null,
      summary: rates ? fromBackend.summary : null,
      suggested_action: rates ? fromBackend.suggested_action : null,
      why: fromBackend.why ?? [],
      missing: applyVouchMissingLine(fromBackend.missing ?? [], vouchState),
    };
  }


  const label = review?.confidence_label ?? "Pending";
  const headline =
    label === "Strong" || label === "Moderate-High"
      ? "High Confidence"
      : label === "Moderate"
        ? "Moderate Confidence"
        : label === "Limited"
          ? "Limited Confidence"
          : "Pending";

  // Build "Connected" as one line per account from the backend payload.
  // Sources of truth (in priority order):
  //  1) connected_sources[] — each entry's corroboration may list multiple
  //     accounts ("Gmail account verified, Amazon account verified"); split
  //     them so each account renders on its own line.
  //  2) evidence_tree (digital / institutional / human / baseline) — any
  //     item with status === "verified".
  const why: string[] = [];
  for (const src of review?.connected_sources ?? []) {
    const status = resolveSourceStatus(src);
    if (status !== "connected" && status !== "confirmed") continue;
    const name = prettySourceName(src.name);
    const corro = src.corroboration as string | undefined;
    const tenure = src.tenure as string | undefined;
    // Split a list of accounts ("Gmail account verified, Amazon account
    // verified") but never inside parentheses: a GitHub row reads "GitHub
    // account connected (@handle, over 5 years old)" and is one line.
    const parts = (corro ?? "")
      .split(/,(?![^()]*\))| and (?![^()]*\))/i)
      .map((s) => s.trim())
      .filter(Boolean);
    if (parts.length === 0) {
      why.push(tenure ? `${name} — ${tenure}` : name);
    } else {
      for (const p of parts) why.push(tenure ? `${p} (${tenure})` : p);
    }
  }
  const treeGroups = [
    review?.evidence_tree?.digital ?? [],
    review?.evidence_tree?.institutional ?? [],
    review?.evidence_tree?.human ?? [],
    review?.evidence_tree?.baseline ?? [],
  ];
  for (const group of treeGroups) {
    for (const item of group) {
      if (item.status === "verified") {
        const line = `${prettySourceName(item.item)} verified`;
        if (!why.includes(line)) why.push(line);
      }
    }
  }

  // Build "Missing" as one line per item the backend reports as missing
  // or partial across every evidence_tree group, plus any connected_source
  // explicitly marked not_connected.
  const missing: string[] = [];
  for (const group of treeGroups) {
    for (const item of group) {
      if (item.status === "missing" || item.status === "partial") {
        const suffix = item.status === "partial" ? " (partial)" : "";
        const line = `${prettySourceName(item.item)}${suffix}`;
        if (!missing.includes(line)) missing.push(line);
      }
    }
  }
  for (const src of review?.connected_sources ?? []) {
    if (resolveSourceStatus(src) === "not_connected") {
      const line = `${prettySourceName(src.name)} not connected`;
      if (!missing.includes(line)) missing.push(line);
    }
  }


  const suggested_action =
    headline === "High Confidence"
      ? `Mark as verified unless your ${copy.reviewingTeam} requires additional signals.`
      : headline === "Limited Confidence"
        ? "Request more verification before completing the review."
        : "Review the evidence below and choose a result.";

  const summary =
    headline === "Pending"
      ? "Verification is still in progress."
      : `DigaVouch found ${headline.toLowerCase()} based on the connected signals.`;

  return {
    headline: rates ? headline : null,
    summary: rates ? summary : null,
    why,
    missing: applyVouchMissingLine(missing, vouchState),
    suggested_action: rates ? suggested_action : null,
  };
}

interface AiSummary {
  narrative: string;
  suggested_read: string;
  generated_at: string;
  model?: string;
}

function deriveAiSummary(review: CaseReview | null): AiSummary | null {
  return review?.ai_summary ?? null;
}

// ------------------------------------------------------------------
// Page
// ------------------------------------------------------------------

// Entrance fade neutralized — was causing a perceived "double load" where the
// per-case skeleton would clear and the header would then fade/slide in. See
// docs/double-load-fix-v4.md. Motion wrapper kept to avoid JSX churn.
const fadeIn = { initial: false as const, animate: { opacity: 1, y: 0 }, transition: { duration: 0 } };

export default function Applicant() {
  const params = useParams<{ id?: string; inquiryId?: string }>();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  useEffect(() => { initClarity(); }, []);

  // The operator's org vertical. Operator surface, so `useOperatorVertical` is
  // the right route here — unlike the two magic-link journeys, whoever is on
  // this page necessarily has a membership (it sits inside ProtectedRoute).
  // ⚠️ `isResolved` matters here in a way it does not on the queue. Until
  // 2026-10-14 an unresolved vertical rendered as leasing, and leasing rates
  // the applicant, so on a cold load a recruiter saw "High Confidence" for a
  // beat. It now reads as unknown, which rates nobody, but the evidence card
  // still holds its skeleton until the answer is in, so a cached or stale
  // leasing answer cannot paint first either.
  const { vertical, isResolved: verticalResolved, me } = useOperatorVertical();
  const copy = copyForVertical(vertical);
  const ratesApplicant = showsApplicantRating(vertical);
  // platform#600 part 1: a sibling of `ratesApplicant`, not a reuse. See
  // `showsAiReviewSummary`'s docblock for why the two must stay independent.
  const showsAiSummaryCard = showsAiReviewSummary(vertical);


  // Accept ?inquiry_id= as a fallback so existing /case-review links can be
  // pointed here during the rollout.
  const inquiryId = params.id ?? params.inquiryId ?? search.get("inquiry_id") ?? null;

  const { cases } = useCases({ includeClosed: true });
  const { data: review, loading: reviewLoading } = useCaseReview(inquiryId);
  const { result: inquiryResult } = useInquiryResult(inquiryId ?? undefined);

  const caseSummary = useMemo(
    () => (inquiryId ? cases.find((x) => x.inquiry_id === inquiryId) ?? null : null),
    [cases, inquiryId],
  );

  const applicantName =
    review?.applicant?.display_name ??
    caseSummary?.applicant_name ??
    toTitleCase(copy.applicantNoun);
  const visibleConnectedSources = useMemo(() => {
    return review?.connected_sources ?? [];
  }, [review?.connected_sources]);
  // Counts for the Summary card: plain tallies of what is listed below it.
  const summaryFacts = useMemo(() => {
    const isVouchSource = (src: CaseReviewSource) => /vouch/i.test(String(src?.name ?? ""));
    // Sign-in accounts only: the backend ids them `oauth:<provider>[:…]`.
    // `resolveSourceStatus` falls back to "connected" for any row without a
    // status, so without this an email or income row would count as an account.
    const isAccountSource = (src: CaseReviewSource) => String(src?.id ?? "").startsWith("oauth:");
    const accounts = visibleConnectedSources.filter((src) => isAccountSource(src) && resolveSourceStatus(src) === "connected").length;
    const vouchSources = visibleConnectedSources.filter(isVouchSource);
    const confirmed = vouchSources.filter((src) => resolveSourceStatus(src) === "confirmed").length;
    const notConfirmed = vouchSources.filter((src) => resolveSourceStatus(src) === "not_confirmed").length;
    return {
      accounts,
      vouchesRequested: vouchSources.length,
      vouchesConfirmed: confirmed,
      vouchesNotConfirmed: notConfirmed,
      vouchesWaiting: vouchSources.length - confirmed - notConfirmed,
    };
  }, [visibleConnectedSources]);

  // The words for the case state and the decision controls. Until the
  // vertical resolves they use the recruiting words, which carry no verdict:
  // a leasing page shows neutral words for a moment, rather than a recruiter
  // seeing "Verified" for a moment.
  const stateVertical: Vertical = verticalResolved ? vertical : "hr_recruiting";
  const status = caseSummary ? getCaseStatus(caseSummary, stateVertical) : null;
  const decisionCopy = reviewDecisionCopy(stateVertical);
  const recruitingReview = isRecruiting(stateVertical);
  const followUp = resolveFollowUpReason(caseSummary?.follow_up_reason);

  // ---------- Decision modals ----------
  const [pendingDecision, setPendingDecision] = useState<DecisionInput | null>(null);
  const [submitting, setSubmitting] = useState<DecisionInput | null>(null);

  // "Request more verification" modal state
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestType, setRequestType] = useState<"identity" | "human_vouch" | null>(null);
  const [requestNote, setRequestNote] = useState("");

  // "Mark as Not Verified" modal state
  const [notVerifiedOpen, setNotVerifiedOpen] = useState(false);
  const [notVerifiedReason, setNotVerifiedReason] = useState<string>("");
  const [notVerifiedOtherNote, setNotVerifiedOtherNote] = useState("");

  useEffect(() => {
    if (!requestOpen) {
      setRequestType(null);
      setRequestNote("");
    }
  }, [requestOpen]);

  // ---------- Other Actions ----------
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);


  // View-notes inline panel
  const [notesPanelOpen, setNotesPanelOpen] = useState(false);
  const autoOpenedRef = useRef(false);
  // Auto-fetch notes when the case is Not Verified so we can surface the
  // recorded reason in the Recommended Next Step card without requiring the
  // operator to expand the View notes panel first.
  const isNotVerifiedCase = caseSummary?.operator_action === "denied" && caseSummary?.status === "completed";
  const notesQuery = useInternalNotes(inquiryId, notesPanelOpen || isNotVerifiedCase);
  const notes = notesQuery.data ?? [];

  // Parse the most recent decision note tagged `[Not Verified · <value>] <label>` for the reason label.
  // Falls back to the structured backend field once it ships (see docs/not-verified-reasons-2026-05-29.md).
  const notVerifiedReasonLabel = useMemo<string | null>(() => {
    if (!isNotVerifiedCase) return null;
    const format = (label: string, free?: string | null): string => {
      const clean = label.replace(/\s*\(Requires Internal Note\)\s*$/i, "").trim();
      const extra = (free ?? "").trim();
      if (!clean) return extra || "";
      return extra ? `${clean} — ${extra}` : clean;
    };
    const structured = review?.not_verified_reason_label;
    const structuredNote = review?.not_verified_note;
    if (structured && structured.trim()) {
      const out = format(structured, structuredNote);
      if (out) return out;
    }
    const re = /\[Not Verified · ([a-z_]+)\]\s*([^\n—]+?)(?:\s*—\s*(.*))?$/i;
    for (const n of notes) {
      const m = n.body?.match(re);
      if (m) {
        const out = format(m[2] ?? "", m[3] ?? "");
        if (out) return out;
      }
    }
    // Fallback: locally cached reason from this operator's submit (survives until
    // backend persists the decision note — see docs/not-verified-reasons-2026-05-29.md).
    if (inquiryId && typeof window !== "undefined") {
      try {
        const raw = window.localStorage.getItem(`dv-nv-reason:${inquiryId}`);
        if (raw) {
          const parsed = JSON.parse(raw) as { label?: string; note?: string };
          const out = format(parsed.label ?? "", parsed.note);
          if (out) return out;
        }
      } catch {
        /* ignore */
      }
    }
    return null;
  }, [isNotVerifiedCase, notes, review, inquiryId]);


  useEffect(() => {
    if (!noteOpen) setNoteDraft("");
  }, [noteOpen]);

  const handleDecision = async (
    decision: DecisionInput,
    note?: string,
    reason?: { value: string; label: string },
  ) => {
    if (!inquiryId) return;
    setSubmitting(decision);
    try {
      const body: { decision: DecisionInput; note?: string; reason?: string; reason_label?: string } = { decision };
      if (note && note.trim()) body.note = note.trim();
      if (reason) {
        body.reason = reason.value;
        body.reason_label = reason.label;
      }
      await reviewerApi.submitDecision(inquiryId, body);
      toast.success(
        decision === "proceed"
          ? decisionCopy.proceedToast
          : decision === "request_more_info"
            ? "More verification requested"
            : decisionCopy.holdToast,
      );
      queryClient.invalidateQueries({ queryKey: ["reviewer", "cases"] });
      queryClient.invalidateQueries({ queryKey: ["reviewer", "sessions"] });
      queryClient.invalidateQueries({ queryKey: ["reviewer", "inquiry-result", inquiryId] });
      queryClient.invalidateQueries({ queryKey: ["reviewer", "case-review", inquiryId] });
      setRequestOpen(false);
    } catch (e) {
      const err = e as { alreadyDecided?: boolean; message?: string };
      toast.error(err.alreadyDecided ? "This case has already been decided" : err.message ?? "Failed to submit");
    } finally {
      setSubmitting(null);
      setPendingDecision(null);
    }
  };

  const handleSendRequest = () => {
    if (!requestType) return;
    const typeLabel = requestType === "identity" ? "Identity verification" : "Human vouch";
    const composed = `[Request: ${typeLabel}]${requestNote.trim() ? `\n${requestNote.trim()}` : ""}`;
    handleDecision("request_more_info", composed);
  };

  const handleSaveNote = async () => {
    if (!inquiryId || !noteDraft.trim()) return;
    setSavingNote(true);
    try {
      await reviewerApi.addNote(inquiryId, noteDraft.trim());
      toast.success("Internal note added");
      setNoteOpen(false);
      queryClient.invalidateQueries({ queryKey: ["reviewer", "notes", inquiryId] });
      queryClient.invalidateQueries({ queryKey: ["reviewer", "inquiry-result", inquiryId] });
      // Auto-open the View notes panel once after the first save so the
      // operator can see their note land in context.
      if (!autoOpenedRef.current) {
        autoOpenedRef.current = true;
        setNotesPanelOpen(true);
      }
    } catch (e) {
      toast.error((e as Error).message ?? "Failed to add note");
    } finally {
      setSavingNote(false);
    }
  };

  // ---------- Render ----------

  if (!inquiryId) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <p className="text-sm text-muted-foreground">Missing inquiry id.</p>
      </div>
    );
  }

  // Note: we intentionally do NOT block the whole page on `reviewLoading`.
  // `/cases/{id}/review` is ~3s on staging while `/inquiries/{id}/result` is
  // ~400ms. Header, status, timeline, and Other Actions come from the fast
  // endpoints and render immediately. Review-dependent sections (recommendation,
  // confidence, evidence tree, connected sources, AI summary) show their own
  // inline placeholder via `reviewLoading && !review`.


  const confidence = deriveConfidencePanel(review, copy, vertical);
  const ai = deriveAiSummary(review);
  const reviewPending = reviewLoading && !review;

  // BF-B5 / ADR-012 R3.4 + R4 — applicant ID verification, as the operator sees
  // it. Read through `(review as any)` for the reason documented at the top of
  // this file: the backend serves both fields (platform#226, #231) and
  // dv-api-contract v0.1.3 doesn't carry them yet. Both parsers return null on
  // anything unrecognised, so a contract that never catches up degrades to
  // rendering nothing rather than to a broken chip.
  // platform#595 — a required ID check that went unmet. Same `as` read as the
  // two fields above, for the same reason.
  const requiredIdv = parseRequiredIdv((review as Record<string, unknown> | null)?.required_idv);
  const idvDisplay = describeApplicantIdvState(
    parseApplicantIdvState((review as Record<string, unknown> | null)?.applicant_idv_state),
    { requiredByFlow: requiredIdv?.required === true },
  );
  const idvNameDisplay = describeIdvNameBinding(
    parseIdvNameBinding((review as Record<string, unknown> | null)?.applicant_idv_name_binding),
  );


  // Prefer the rich ActivityEvent stream; fall back to legacy timeline.
  // `canShowRating` folds in `verticalResolved` — see the docblock on
  // `cleanTimelineEvent` for why the Timeline needs that hold too.
  const canShowRating = canShowApplicantRating(vertical, verticalResolved);
  const timeline = (inquiryResult?.events ?? []).map((ev) => ({
    label: cleanTimelineEvent(ev.kind.replace(/_/g, " "), canShowRating),
    at: ev.at,
  }));
  const legacyTimeline = (review?.verification_timeline ?? []).map((ev) => ({
    label: cleanTimelineEvent(ev.event, canShowRating),
    at: ev.date,
  }));
  const events = timeline.length > 0 ? timeline : legacyTimeline;

  // Collapsible summaries for User Data Reviewed + Timeline.
  const CONNECTED_STATUSES = new Set(["connected", "verified", "partial", "confirmed"]);
  const signalCount = visibleConnectedSources.filter((src) => {
    const name = typeof src?.name === "string" ? src.name.toLowerCase() : "";
    if (/vouch/.test(name)) return false;
    return CONNECTED_STATUSES.has(resolveSourceStatus(src));
  }).length;
  const sourceCount = new Set(
    visibleConnectedSources
      .filter((src) => CONNECTED_STATUSES.has(resolveSourceStatus(src)))
      .map((src) => (typeof src?.name === "string" ? src.name : String(src?.name ?? "")))
      .filter(Boolean),
  ).size;
  const lastEventAt = events.length > 0
    ? events.reduce((max, ev) => (ev.at && ev.at > max ? ev.at : max), events[0].at)
    : null;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-4">
      {/* Both this and the close-case redirect above used to be a hardcoded
          "/dashboard", which sends a recruiting operator to the leasing queue.
          `operatorHome` is the same resolver `/` already uses (#160). */}
      <button
        onClick={() => navigate(operatorHome(me))}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        {queueBackLabel(vertical)}
      </button>

      <motion.header {...fadeIn}>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-bold tracking-tight text-foreground">{applicantName}</h1>
          {status && (
            <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${status.tileClass}`}>
              {detailStatusLabel(status.kind, stateVertical)}
            </span>
          )}
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          {copy.locatorLabel}: {caseSummary?.property?.trim() ? caseSummary.property : "—"}
        </p>
        {/* Same predicate as the Send-Request dialog's reason field and the
            queue's Verification Reasons panel — the three must not drift. A
            vertical with no reason to record has no reason to report either. */}
        {showsFollowUpReason(vertical) && (
          <p className="text-sm text-muted-foreground mt-1">
            {toTitleCase(copy.applicantNoun)} Verification Reason: {followUp?.label ?? caseSummary?.follow_up_reason ?? "Not Enough Information to Verify Identity"}
          </p>
        )}
      </motion.header>

      {/* AI Review Summary.

          platform#600 part 1: hidden entirely for recruiting, not merely
          emptied. The card holds a model-written `narrative` plus a section
          headed "Suggested read" addressed to the person making the hiring
          decision; the founders' 2026-09-17 rule (recruiters see the Trust
          Checkpoint state, the evidence and factual notes, never a score or
          anything reading as a recommendation) reaches this card the same
          way it reached the confidence headline below.

          ⚠️ The skeleton is held on `!verticalResolved` too, before
          `showsAiSummaryCard` is even consulted. Until 2026-10-14 `vertical`
          defaulted to leasing while the operator's membership was still
          loading, so without the hold a recruiter saw the real narrative for
          a beat on a cold load. Same trap PR #204 hit on the confidence card,
          same fix: nothing but a skeleton renders until the vertical answer
          is in. */}
      {(!verticalResolved || showsAiSummaryCard) && (
        <SectionCard label="AI Review Summary">
          {reviewPending || !verticalResolved ? (
            <div className="border-l-4 border-border pl-4 space-y-2">
              <div className="h-3 w-24 bg-muted rounded animate-pulse" />
              <div className="h-3 w-full bg-muted rounded animate-pulse" />
              <div className="h-3 w-5/6 bg-muted rounded animate-pulse" />
            </div>
          ) : ai ? (
            <div className="border-l-4 border-primary/70 pl-4">
              <div className="text-sm font-semibold text-foreground mb-2">Summary</div>
              <p className="text-sm text-foreground leading-relaxed">{ai.narrative}</p>
              <div className="mt-4">
                <div className="text-xs font-bold uppercase tracking-wider text-primary mb-1.5">Suggested read</div>
                <p className="text-sm text-muted-foreground leading-relaxed">{ai.suggested_read}</p>
              </div>
              <div className="text-xs text-muted-foreground mt-3 inline-flex items-center gap-1.5">
                <Sparkles className="w-3 h-3 text-primary/70" aria-hidden="true" />
                <span>AI Summary · {formatAbsoluteDateTime(ai.generated_at)}</span>
              </div>
            </div>
          ) : (
            <div className="border-l-4 border-border pl-4">
              <p className="text-sm text-muted-foreground">AI summary not generated yet.</p>
            </div>
          )}
        </SectionCard>
      )}


      {/* Identity evidence.

          platform#583 — one card, two shapes. Under leasing it leads with
          the rating; under recruiting it leads with the evidence and there is
          no rating at all. The CONNECTED / MISSING grids and the ID
          VERIFICATION block below them are identical in both, because they are
          the evidence and the evidence was never in question. */}
      {/* The title is held with the rest: `canShowRating`, not `ratesApplicant`,
          so the card never reads "Identity Confidence" over its skeleton
          before the vertical is known. */}
      <SectionCard label={canShowRating ? "Identity Confidence" : "Identity Evidence"}>
        {reviewPending || !verticalResolved ? (
          <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-3">
            <div className="h-4 w-40 bg-muted rounded animate-pulse" />
            <div className="h-3 w-3/4 bg-muted rounded animate-pulse" />
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="h-20 bg-muted/60 rounded animate-pulse" />
              <div className="h-20 bg-muted/60 rounded animate-pulse" />
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-sky-200 bg-sky-50/60 p-4">
            {/* The rating and the pill it sits beside. Both are `null` for a
                vertical that does not rate the applicant, so there is nothing
                to render and nothing to lay out around. */}
            {confidence.headline && (
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-base font-bold text-sky-900">{confidence.headline}</div>
                  <p className="text-sm text-foreground mt-1 max-w-xl">{confidence.summary}</p>
                </div>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-sky-100 text-sky-800">
                  AI-assisted recommendation
                </span>
              </div>
            )}
            <div className={`grid sm:grid-cols-2 gap-3 ${confidence.headline ? "mt-4" : ""}`}>
              <div className="rounded-lg bg-card border border-emerald-200 p-3">
                <div className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-1.5">CONNECTED</div>
                {confidence.why.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No supportive signals yet.</p>
                ) : (
                  <ul className="text-sm text-foreground space-y-1.5">
                    {confidence.why.map((w, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                        <span>{w}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="rounded-lg bg-card border border-border p-3">
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">MISSING</div>
                {confidence.missing.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Nothing flagged as missing.</p>
                ) : (
                  <ul className="text-sm text-foreground space-y-1.5">
                    {confidence.missing.map((m, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Circle className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                        <span>{m}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/*
              BF-B5 / ADR-012 R3.4 — ID verification state.

              It sits inside this card, under CONNECTED / MISSING, because it is
              part of the identity picture rather than a separate concern — and
              because MISSING alone cannot say WHY the evidence is absent. An
              applicant who opted out and one who never started both read as a
              gap up there; only this block tells them apart, which is the entire
              point of ADR-012 R3.4.

              Nothing here is gated on, and nothing here lowers a score. It is
              shown so the operator can ask, not so the product can judge.
            */}
            {inquiryId && hasRequiredIdvToShow(requiredIdv) && (
              <div className="mt-3">
                <RequiredIdvReviewCard inquiryId={inquiryId} requiredIdv={requiredIdv} />
              </div>
            )}
            {(idvDisplay || idvNameDisplay) && (
              <div className="mt-3 rounded-lg bg-card border border-border p-3">
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
                  ID VERIFICATION
                </div>
                <div className="space-y-2.5">
                  {idvDisplay && (
                    <div>
                      <ApplicantIDVStateBadge label={idvDisplay.label} tone={idvDisplay.tone} />
                      {idvDisplay.detail && (
                        <p className="text-sm text-muted-foreground mt-1.5">{idvDisplay.detail}</p>
                      )}
                    </div>
                  )}
                  {idvNameDisplay && (
                    <div>
                      <ApplicantIDVStateBadge
                        label={idvNameDisplay.label}
                        tone={idvNameDisplay.tone}
                      />
                      {idvNameDisplay.detail && (
                        <p className="text-sm text-muted-foreground mt-1.5">
                          {idvNameDisplay.detail}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

          </div>
        )}
      </SectionCard>


      {/* Why this was requested — removed; reason is in the header subtitle */}

      {/* Summary: facts only, computed here from what this page already
          holds. It replaced "Recommended Next Step" (review call 2026-10-15):
          a recommended next step about a person is a decision aid, and an
          EEOC risk in hiring. No model writes it; nothing in it recommends. */}
      {caseSummary && (
        <SectionCard label="Summary">
          <ul className="text-sm text-foreground space-y-1.5">
            <li>
              <span className="text-muted-foreground">Accounts connected:</span> {summaryFacts.accounts}
            </li>
            <li>
              <span className="text-muted-foreground">Vouches:</span>{" "}
              {summaryFacts.vouchesRequested === 0
                ? "none requested"
                : [
                    `${summaryFacts.vouchesRequested} requested`,
                    `${summaryFacts.vouchesConfirmed} confirmed`,
                    summaryFacts.vouchesNotConfirmed > 0 ? `${summaryFacts.vouchesNotConfirmed} not confirmed` : null,
                    summaryFacts.vouchesWaiting > 0 ? `${summaryFacts.vouchesWaiting} waiting for a reply` : null,
                  ].filter(Boolean).join(", ")}
            </li>
            {caseSummary.operator_action === "approved" && (
              <li>
                <span className="text-muted-foreground">Review recorded:</span>{" "}
                {recruitingReview ? "no findings" : "verified"}
              </li>
            )}
            {isNotVerifiedCase && (
              <li>
                <span className="text-muted-foreground">Review recorded:</span>{" "}
                {recruitingReview ? "findings noted" : "not verified"} ({notVerifiedReasonLabel ?? "reason on file"})
              </li>
            )}
            {caseSummary.updated_at && (
              <li>
                <span className="text-muted-foreground">Last updated:</span> {formatAbsoluteDateTime(caseSummary.updated_at)}
              </li>
            )}
          </ul>
        </SectionCard>
      )}

      {/* Signals reviewed */}
      <Card>
        <CardContent className="p-5">
          <Collapsible defaultOpen>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                  Verification Signals &amp; Requests
                </div>
                <div className="text-sm text-foreground">
                  Signals connected and outstanding verification requests.
                </div>
              </div>
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline shrink-0 mt-1 [&[data-state=open]>svg]:rotate-180"
                >
                  View details
                  <ChevronDown className="w-3.5 h-3.5 transition-transform" />
                </button>
              </CollapsibleTrigger>
            </div>
            <CollapsibleContent className="pt-4">
              {visibleConnectedSources.length === 0 ? (
                <p className="text-sm text-muted-foreground">No signals connected yet.</p>
              ) : (
                <div className="space-y-3">
                  {visibleConnectedSources.map((src) => {
                    const status = resolveSourceStatus(src);
                    const rawName = typeof src.name === "string" ? src.name.toLowerCase() : "";
                    const isVouch = /vouch/.test(rawName);
                    const confirmedApplicant = src.confirmed_applicant;
                    const meta = SOURCE_BADGE[status];
                    const detail = buildSourceDetail(src);
                    // Backend brief §4 — progressive enhancement for human vouches.
                    const voucher = src.voucher;
                    // C-3: the backend emits status='not_confirmed' directly and only
                    // sends verified_facts on outcome=confirm (verified 2026-07-04,
                    // results.ts:1089-1123). The former FE not_confirmed override +
                    // verified_facts suppression are gone — trust the backend payload.
                    const verifiedFacts = src.verified_facts ?? [];
                    const hasVouchExtras =
                      isVouch && (voucher?.display_name || typeof confirmedApplicant === "boolean" || verifiedFacts.length > 0);
                    return (
                      <div
                        key={src.id}
                        className="rounded-lg border border-border p-3.5 flex items-start justify-between gap-3"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-foreground">{prettySourceName(src.name)}</div>
                          {detail && <div className="text-xs text-muted-foreground mt-0.5">{detail}</div>}
                          {hasVouchExtras && (
                            <div className="mt-2 space-y-1">
                              {voucher?.display_name && (
                                <div className="text-xs text-foreground">
                                  <span className="font-medium">{voucher.display_name}</span>
                                  {voucher.relationship ? ` (${voucher.relationship})` : ""}
                                  {typeof confirmedApplicant === "boolean" && (
                                    <span className="text-muted-foreground">
                                      {" — "}
                                      {confirmedApplicant ? "confirmed applicant" : "did not confirm applicant"}
                                    </span>
                                  )}
                                </div>
                              )}
                              {verifiedFacts.length > 0 && (
                                <ul className="list-disc pl-4 text-xs text-muted-foreground space-y-0.5">
                                  {verifiedFacts.map((f, i) => (
                                    <li key={i}>{f}</li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          )}
                        </div>
                        <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${meta.cls}`}>
                          {meta.text}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
      </Card>

      {/* Timeline */}
      <Card>
        <CardContent className="p-5">
          <Collapsible defaultOpen={false}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                  Timeline
                </div>
                <div className="text-sm text-foreground">
                  {lastEventAt
                    ? <>Last activity: <span className="text-muted-foreground">{formatAbsoluteDateTime(lastEventAt)}</span></>
                    : <span className="text-muted-foreground">Last activity unavailable</span>}
                </div>
              </div>
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline shrink-0 mt-1 [&[data-state=open]>svg]:rotate-180"
                >
                  View timeline
                  <ChevronDown className="w-3.5 h-3.5 transition-transform" />
                </button>
              </CollapsibleTrigger>
            </div>
            <CollapsibleContent className="pt-4">
              {events.length === 0 ? (
                <p className="text-sm text-muted-foreground">No activity recorded yet.</p>
              ) : (
                <ul className="space-y-2.5">
                  {events.map((ev, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-2 h-2 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                      <div className="flex-1 flex justify-between items-baseline gap-3 flex-wrap">
                        <span className="text-sm text-foreground">{ev.label}</span>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {formatAbsoluteDateTime(ev.at)}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
      </Card>


      {/* Complete Review */}
      <SectionCard label="Complete Review">
        {/* ⚠️ LEFT LEASING ON PURPOSE, with the two decision-modal disclaimers
            below it and `REGULATORY_NOTE` in caseStatus.ts. These are the
            scope-of-service statements, not vocabulary: they are written
            against a tenant-screening posture, the employment equivalent sits
            under a different regime, and the company constitution's sixth core
            value commits to real legal advice before entering a vertical.
            `verticalCopy.ts` carries a test pinning them OUT of the copy deck.
            Design record §6 / §7. The "Important note" below is hidden for
            recruiting (`showsRentalScopeNote`) rather than reworded, and so is
            this paragraph (Juan, 2026-10-15): hidden, not reworded, so no new
            scope statement is made for hiring. */}
        {!recruitingReview && (
          <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
            By selecting an option below, you confirm you have reviewed the verification signals and
            understand DigaVouch supports identity verification only and does not make the final
            leasing decision.
          </p>
        )}


        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            disabled={submitting !== null}
            onClick={() => setRequestOpen(true)}
            className="flex-1 text-left rounded-xl border-2 border-border bg-card hover:bg-muted/40 disabled:opacity-50 disabled:cursor-not-allowed p-4 transition-colors"
          >
            <Info className="w-5 h-5 text-foreground mb-2" />
            <div className="text-sm font-bold text-foreground mb-1">Request More Verification</div>
            <div className="text-xs text-muted-foreground">
              Signals are incomplete; keep the case open for more data.
            </div>
          </button>
          <button
            type="button"
            disabled={submitting !== null}
            onClick={() => setPendingDecision("proceed")}
            className="flex-1 text-left rounded-xl border-2 border-emerald-300 bg-emerald-50/60 hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed p-4 transition-colors"
          >
            <CheckCircle2 className="w-5 h-5 text-emerald-700 mb-2" />
            <div className="text-sm font-bold text-emerald-800 mb-1">{decisionCopy.proceedLabel}</div>
            <div className="text-xs text-emerald-900/80">{decisionCopy.proceedHint}</div>
          </button>
          <button
            type="button"
            disabled={submitting !== null}
            onClick={() => {
              // Pre-select recommended reason if we can derive one; otherwise leave blank.
              const missing = (confidence?.missing ?? []) as string[];
              const hasMismatch = missing.some((m) => /mismatch/i.test(m));
              const recommended = hasMismatch ? "linked_account_mismatch" : "";
              setNotVerifiedReason(recommended);
              setNotVerifiedOtherNote("");
              setNotVerifiedOpen(true);
            }}
            className="flex-1 text-left rounded-xl border-2 border-rose-200 bg-rose-50/60 hover:bg-rose-50 disabled:opacity-50 disabled:cursor-not-allowed p-4 transition-colors"
          >
            <XCircle className="w-5 h-5 text-rose-700 mb-2" />
            <div className="text-sm font-bold text-rose-800 mb-1">{decisionCopy.holdLabel}</div>
            <div className="text-xs text-rose-900/80">{decisionCopy.holdHint}</div>
          </button>
        </div>
      </SectionCard>


      {/* Important Note: rental wording, so leasing only (recruiting has no
          counsel-approved equivalent yet), and held until the vertical
          resolves so it never flashes for a recruiter. */}
      {verticalResolved && showsRentalScopeNote(vertical) && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-bold text-amber-900">Important note</div>
              <p className="text-sm text-amber-900/85 mt-1 leading-relaxed">{REGULATORY_NOTE}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Internal notes. The three other actions that sat here (send reminder,
          close request, resend link) were removed in the review call of
          2026-10-15; only the notes remain. */}
      <SectionCard label="Internal notes">
        <div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
            <Button
              variant="outline"
              size="sm"
              className="justify-start"
              onClick={() => setNoteOpen(true)}
            >
              <MessageSquarePlus className="w-4 h-4" />
              Add internal note
            </Button>

            <Button
              variant="outline"
              size="sm"
              className="justify-between"
              onClick={() => setNotesPanelOpen((v) => !v)}
              aria-expanded={notesPanelOpen}
            >
              <span className="inline-flex items-center gap-2">
                <FileText className="w-4 h-4" />
                View notes
                {notes.length > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-primary/10 text-primary text-[11px] font-semibold">
                    {notes.length}
                  </span>
                )}
              </span>
              <ChevronDown
                className={`w-4 h-4 transition-transform ${notesPanelOpen ? "rotate-180" : ""}`}
              />
            </Button>
          </div>
        </div>

        <AnimatePresence initial={false}>
          {notesPanelOpen && (
            <motion.div
              key="notes-panel"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="overflow-hidden"
            >
              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex items-baseline justify-between gap-3 mb-3">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Internal notes</div>
                    <div className="text-xs text-muted-foreground">
                      Operator-only — not shared with applicant.
                    </div>
                  </div>
                </div>

                {notesQuery.isLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-16 w-full" />
                    <Skeleton className="h-16 w-full" />
                  </div>
                ) : notesQuery.isError ? (
                  <div className="text-sm text-muted-foreground">
                    Could not load notes.{" "}
                    <button
                      type="button"
                      className="underline text-foreground hover:text-primary"
                      onClick={() => notesQuery.refetch()}
                    >
                      Retry
                    </button>
                  </div>
                ) : notes.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No internal notes yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {notes.map((n) => (
                      <li key={n.note_id} className="rounded-lg bg-muted/40 p-3.5">
                        <p className="text-sm text-foreground whitespace-pre-wrap break-words">
                          {n.body}
                        </p>
                        <div
                          className="mt-2 text-xs text-muted-foreground"
                          title={formatAbsoluteDateTime(n.created_at)}
                        >
                          {n.author_actor || "operator"} · {formatRelativeTime(n.created_at)}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </SectionCard>


      {/* ---------- Decision confirm modal (Verified only) ---------- */}
      <AlertDialog open={pendingDecision !== null} onOpenChange={(o) => !o && setPendingDecision(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            {/* Recruiting states what the action records and nothing more.
                The leasing wording carries a rental scope statement, which
                stays leasing only, the same way the Important note below is
                hidden for recruiting rather than reworded. */}
            {recruitingReview ? (
              <>
                <AlertDialogTitle>Complete your review?</AlertDialogTitle>
                <AlertDialogDescription>
                  You are marking this case as <strong>{statusLabel("verified", stateVertical)}</strong>.
                  This records that your team reviewed the evidence the applicant provided. It is
                  saved to the case history.
                </AlertDialogDescription>
              </>
            ) : (
              <>
                <AlertDialogTitle>Complete DigaVouch review?</AlertDialogTitle>
                <AlertDialogDescription>
                  You are marking this case as <strong>Verified</strong>. This completes the DigaVouch
                  identity-confidence review. Final leasing decisions remain with the property team. This action will
                  be recorded in the case history.
                </AlertDialogDescription>
              </>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting !== null}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={submitting !== null}
              onClick={() => pendingDecision && handleDecision(pendingDecision)}
            >
              Confirm result
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ---------- Mark as Not Verified reason modal ---------- */}
      <Dialog open={notVerifiedOpen} onOpenChange={(o) => !submitting && setNotVerifiedOpen(o)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{decisionCopy.holdDialogTitle}</DialogTitle>
            <DialogDescription>{decisionCopy.holdDialogBody}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {notVerifiedReason && NOT_VERIFIED_REASONS.find((r) => r.value === notVerifiedReason) && (() => {
              const rec = NOT_VERIFIED_REASONS.find((r) => r.value === notVerifiedReason)!;
              const missing = (confidence?.missing ?? []) as string[];
              const hasMismatch = missing.some((m) => /mismatch/i.test(m));
              const wasRecommended = hasMismatch && rec.value === "linked_account_mismatch";
              if (!wasRecommended) return null;
              return (
                <div className="rounded-lg border border-violet-200 bg-violet-50/60 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-violet-700">
                    Recommended reason
                  </div>
                  <div className="text-sm font-semibold text-foreground mt-1">{rec.label}</div>
                  <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    Based on the verification signals, a connected account appears to be owned by
                    someone other than the applicant.
                  </div>
                </div>
              );
            })()}

            <div>
              <Label className="text-sm font-semibold text-foreground">Reason</Label>
              <Select value={notVerifiedReason} onValueChange={setNotVerifiedReason}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  {notVerifiedReasonsFor(recruitingReview).map((r) => (
                    <SelectItem key={r.value} value={r.value} title={notVerifiedReasonDesc(r.value, recruitingReview)}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {notVerifiedReason && (
                <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                  {notVerifiedReasonDesc(notVerifiedReason, recruitingReview)}
                </p>
              )}
            </div>

            {notVerifiedReason === "other" && (
              <div>
                <Label className="text-sm font-semibold text-foreground">
                  Internal note <span className="text-destructive">*</span>
                </Label>
                <Textarea
                  className="mt-2"
                  rows={3}
                  placeholder="Briefly describe the reason for internal records."
                  value={notVerifiedOtherNote}
                  onChange={(e) => setNotVerifiedOtherNote(e.target.value)}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setNotVerifiedOpen(false)}
              disabled={submitting !== null}
            >
              Cancel
            </Button>
            <Button
              disabled={
                submitting !== null ||
                !notVerifiedReason ||
                (notVerifiedReason === "other" && !notVerifiedOtherNote.trim())
              }
              onClick={async () => {
                const reason = NOT_VERIFIED_REASONS.find((r) => r.value === notVerifiedReason);
                if (!reason) return;
                const freeText = notVerifiedReason === "other" ? notVerifiedOtherNote.trim() : "";
                // C-3: send the reason as structured fields (reason + reason_label →
                // backend not_verified_reason/_label columns, shipped) and the
                // operator's free text as the note. No more embedded
                // `[Not Verified · x]` prefix. The parse in notVerifiedReasonLabel
                // stays as a legacy fallback for rows written before this change.
                // Cache locally so the Recommended Next Step copy can surface the
                // reason immediately, even if backend drops the decision note.
                if (inquiryId && typeof window !== "undefined") {
                  try {
                    window.localStorage.setItem(
                      `dv-nv-reason:${inquiryId}`,
                      JSON.stringify({ value: reason.value, label: reason.label, note: freeText }),
                    );
                  } catch {
                    /* ignore */
                  }
                }
                await handleDecision("hold", freeText || undefined, { value: reason.value, label: reason.label });
                setNotVerifiedOpen(false);
              }}
            >
              {submitting === "hold" ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Submitting…
                </>
              ) : (
                "Submit"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* ---------- Request more verification modal ---------- */}
      <Dialog open={requestOpen} onOpenChange={setRequestOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request more verification</DialogTitle>
            <DialogDescription>
              Keep this case open and ask the applicant to provide an additional signal.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-sm font-semibold text-foreground">What do you want to request?</Label>
              <RadioGroup
                value={requestType ?? ""}
                onValueChange={(v) => setRequestType(v as "identity" | "human_vouch")}
                className="mt-2"
              >
                <label className="flex items-center gap-2 cursor-pointer rounded-md border border-border p-3 hover:bg-muted/40">
                  <RadioGroupItem value="identity" id="req-identity" />
                  <span className="text-sm text-foreground">Identity verification</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer rounded-md border border-border p-3 hover:bg-muted/40">
                  <RadioGroupItem value="human_vouch" id="req-human-vouch" />
                  <span className="text-sm text-foreground">Human vouch</span>
                </label>
              </RadioGroup>
            </div>

            <div>
              <Label htmlFor="req-note" className="text-sm font-semibold text-foreground">
                Add note <span className="text-xs font-normal text-muted-foreground">(optional)</span>
              </Label>
              <Textarea
                id="req-note"
                value={requestNote}
                onChange={(e) => setRequestNote(e.target.value.slice(0, 2000))}
                placeholder="Optional message for the applicant or internal context"
                className="mt-2"
                rows={3}
                maxLength={2000}
              />
              <div
                className={`mt-1 text-right text-xs ${
                  requestNote.length >= 2000
                    ? "text-destructive"
                    : requestNote.length >= 1800
                      ? "text-amber-600"
                      : "text-muted-foreground"
                }`}
              >
                {requestNote.length} / 2000
              </div>
            </div>


            <p className="text-xs text-muted-foreground">Send request now</p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRequestOpen(false)} disabled={submitting !== null}>
              Cancel
            </Button>
            <Button onClick={handleSendRequest} disabled={!requestType || submitting !== null}>
              <Send className="w-4 h-4" />
              Send request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* ---------- Add note modal ---------- */}
      <AlertDialog open={noteOpen} onOpenChange={setNoteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Add an internal note</AlertDialogTitle>
            <AlertDialogDescription>
              Notes are visible to operators in this organization. They are not shared with the applicant.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value.slice(0, 2000))}
            placeholder="What did you observe?"
            rows={4}
            maxLength={2000}
          />
          <div
            className={`-mt-2 text-right text-xs ${
              noteDraft.length >= 2000
                ? "text-destructive"
                : noteDraft.length >= 1800
                  ? "text-amber-600"
                  : "text-muted-foreground"
            }`}
          >
            {noteDraft.length} / 2000
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={savingNote}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={savingNote || !noteDraft.trim()}
              onClick={(e) => {
                e.preventDefault();
                handleSaveNote();
              }}
            >
              Save note
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}

function SectionCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">{label}</div>
        {children}
      </CardContent>
    </Card>
  );
}
